import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type SubTabConfig<ST = void> =
    | {
          defaultSubTab: ST;
          tabs: ST[];
      }
    | undefined;

const parseHash = <T extends string, ST>(
    hash: string,
    tabIds: T[],
    defaultTab: T,
    subTabs?: Record<T, SubTabConfig<ST>>
): { tabId: T; subTabId: ST | undefined } => {
    const currentTabId = hash.split('-')[0];
    const tabId = Object.values(tabIds).find((id) => (subTabs ? id === currentTabId : id === hash)) ?? defaultTab;
    let subTabId: ST | undefined;
    const subTabConfig = subTabs?.[tabId];
    if (subTabConfig) {
        const hashSubTab = hash.split('-').pop();
        const { tabs, defaultSubTab } = subTabConfig;
        subTabId = (tabs && Object.values(tabs).find((id) => id === hashSubTab)) ?? defaultSubTab;
    }
    return { tabId, subTabId };
};

const buildHash = (tabId: string, subTabId?: unknown): string => (subTabId ? `${tabId}-${subTabId}` : tabId);

// nether Tabs or SubTabs should contain a dash (-) as this is used to separate Tabs from SubTabs in the url path
export const useTabbedPane = <T extends string, ST = void>(
    tabIds: T[],
    defaultTab: T,
    subTabs?: Record<T, SubTabConfig<ST>>
) => {
    const location = useLocation();
    const navigate = useNavigate();
    const hash = location.hash?.substring(1) || '';

    const { tabId: tabIdFromUrl, subTabId: subTabIdFromUrl } = parseHash(hash, tabIds, defaultTab, subTabs);

    const [activeTabId, setActiveTabIdState] = useState<T>(tabIdFromUrl);
    const [activeSubTabId, setActiveSubTabIdState] = useState<ST | undefined>(subTabIdFromUrl);

    // Tracks the hash we navigated to programmatically so we can ignore the
    // resulting location update in the URL→state sync effect.
    const pendingHash = useRef<string | null>(null);

    const setActiveTabId = useCallback(
        (tabId: T) => {
            const newHash = buildHash(tabId, activeSubTabId);
            setActiveTabIdState(tabId);
            if (newHash !== hash) {
                pendingHash.current = newHash;
                navigate(`#${newHash}`);
            }
        },
        [activeSubTabId, hash, navigate]
    );

    const setActiveSubTabId = useCallback(
        (subTabId: ST) => {
            const newHash = buildHash(activeTabId, subTabId);
            setActiveSubTabIdState(subTabId);
            if (newHash !== hash) {
                pendingHash.current = newHash;
                navigate(`#${newHash}`);
            }
        },
        [activeTabId, hash, navigate]
    );

    // Sync state when the URL changes externally (back/forward navigation or deeplinks)
    useEffect(() => {
        if (pendingHash.current !== null) {
            if (pendingHash.current === hash) {
                pendingHash.current = null;
            }
            return;
        }
        setActiveTabIdState(tabIdFromUrl);
        setActiveSubTabIdState(subTabIdFromUrl);
    }, [hash]);

    return { activeTabId, setActiveTabId, hash, activeSubTabId, setActiveSubTabId };
};
